import React from 'react';
import PropTypes from 'prop-types';
import { Translate } from "react-localize-redux";
import QS from "query-string";
import { CS_FILTERS_QUERY_SEPARATOR, T_APP_ICONS } from "../../../models/constants/Constants_Shared";
import CSBaseComponent from "../../CSBaseComponent";
import Fa from "../Icons/Fa";
import FiltersModal from "./FiltersModal";
import is from "../../../utils/is";
import { Process_formatLIKEQuerySelector } from "../../../utils/processors";
import { getModifiedState } from "../../../utils/utils";
import { E_Modification } from "../../../store/Constants_StoreShared";
import withRestrictions from "../../shared/Restrictions/withRestrictions";
import { isRestricted } from "../../../utils/restrictionUtils";

class Filter extends CSBaseComponent {
	constructor(props) {
		super(props);

		this.state = {
			...this.state,
			filtersData: {},
		};
	}

	componentDidMount() {
		super.componentDidMount();
		const {onFiltersReady} = this.props;

		onFiltersReady(this._unifyFilterData());
	}

	componentDidUpdate(prevProps, prevState, snapshot) {
		const {filtersOpen, applyFilters} = this.props;

		if(!is.equal(filtersOpen, prevProps.filtersOpen)) {
			if(filtersOpen) {
				this._openFilters();
			}
		}

		if(!is.equal(applyFilters, prevProps.applyFilters)) {
			this._applyFilters();
		}
	}

	render() {
		return null;
	}

	open() {
		this._openFilters();
	}

	_openFilters() {
		const {filters, onFiltersClosed} = this.props;

		window.modal.open({
			body: (
				<FiltersModal
					filters={this._processFilters(filters)}
					onFiltersModify={this._handleFilterUpdate.bind(this)}
				/>
			),
			actionButtons: [
				{
					icon: <Fa icon={"filter-slash"} custom={true} />,
					action: () => {
						this._clearFilters();
						window.modal.close();
					},
					tippy: <Translate id={`filters_clearFilters`} />,
					className: "remove-button"
				},
				{
					icon: T_APP_ICONS.SAVE,
					tippy: <Translate id={"apply"}/>,
					action: () => {
						this._applyFilters();
						window.modal.close();
					}
				}
			]
		}, () => onFiltersClosed())
	}

	_processFilters(filters) {
		const {hasAccess} = this.props;
		const appliedValues = this._getAppliedQueryValues();

		return filters
			.filter(filter => filter && filter.field && filter.type && !filter.hidden && !isRestricted(filter, hasAccess, filter))
			.map(filter => ({
				//Reflect the currently applied value (from the query string) so reopening the modal keeps the selection
				...filter,
				queryValue: appliedValues[filter.key] !== undefined ? appliedValues[filter.key] : '',
			}));
	}

	_unifyFilterData(data = {}) {
		const {filters} = this.props;
		const appliedValues = this._getAppliedQueryValues();

		return filters.map(filter => {
			const {key, operator, value: defaultValue} = filter;
			//Untouched filters fall back to their currently applied value so they are not dropped on re-apply
			let appliedValue = appliedValues[key] !== undefined ? appliedValues[key] : filter.queryValue;
			let value = data[key] !== undefined ? data[key] : (appliedValue || defaultValue);

			return {
				...filter,
				value: operator === "LIKE" ? Process_formatLIKEQuerySelector(value) : value,
				join: filter.join || "AND",
			}
		}).filter(filter => is.valid(filter.value));
	}

	/**
	 * Reads the currently applied filter values straight from the query string,
	 * keyed by filter key. The query string is the source of truth for applied
	 * filters (it is updated on every apply/clear), whereas the queryValue carried
	 * on the filter props is only computed once at mount.
	 * @returns {Object<String, String>}
	 */
	_getAppliedQueryValues() {
		let values = {};

		(QS.parse(window.location.search).filters || '').split(CS_FILTERS_QUERY_SEPARATOR).forEach(entry => {
			let separatorIndex = `${entry}`.indexOf(":");
			if (separatorIndex > 0) {
				values[entry.slice(0, separatorIndex)] = entry.slice(separatorIndex + 1);
			}
		});

		return values;
	}

	_applyFilters() {
		const {onFiltersApply} = this.props;
		const {filtersData} = this.state;

		onFiltersApply(this._unifyFilterData(filtersData))
	}

	_clearFilters() {
		this.props.onFiltersApply([]);
	}

	_handleFilterUpdate(data) {
		this.setState(state => getModifiedState(state, E_Modification.ITEM_SET, `filtersData`, data));
	}
}

Filter.propTypes = {
	/**
	 * @type {T_FilterItemModel[]}
	 */
	filters: PropTypes.array, //External data

	applyFilters: PropTypes.bool, //Listener value (any change should trigger some function regardless of the value)

	onFiltersApply: PropTypes.func.isRequired,
	onFiltersReady: PropTypes.func.isRequired,
	onFiltersClosed: PropTypes.func,
};

Filter.stateTypes = {
	filtersData: PropTypes.object
};

Filter.defaultProps = {
	onFiltersApply: () => {
		console.warn("onFiltersApply function is undefined");
		return [];
	}
};

export default withRestrictions(Filter);
